import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircleHeart, NotebookPen, SmilePlus, Sparkles, AlertCircle, Flame, CheckCircle2 } from "lucide-react";

const PURPLE = "https://images.unsplash.com/photo-1526137844794-45f1041f397a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxncm91bmRpbmclMjBuYXR1cmV8ZW58MHx8fHwxNzgyMjEyNDAxfDA&ixlib=rb-4.1.0&q=85";

const Dashboard = () => {
  const { user } = useAuth();
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/dashboard/summary").then(({ data }) => setS(data)).catch(() => {}); }, []);

  const stats = [
    { label: "Journal entries", value: s?.journal_count ?? "—" },
    { label: "Conversations", value: s?.chat_count ?? "—" },
    { label: "7-day mood avg", value: s?.recent_mood_avg ?? "—" },
  ];

  const reminderDue = (() => {
    if (!s?.reminder_enabled || s?.checked_in_today || !s?.reminder_time) return false;
    const [h, m] = s.reminder_time.split(":").map(Number);
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes() >= h * 60 + m;
  })();

  const quick = [
    { to: "/app/companion", icon: MessageCircleHeart, title: "Talk it through", text: "Open a session with your companion." },
    { to: "/app/journal", icon: NotebookPen, title: "Write a journal", text: "Let your thoughts land on the page." },
    { to: "/app/mood", icon: SmilePlus, title: "Log your mood", text: "A small check-in with yourself." },
    { to: "/app/tools", icon: Sparkles, title: "Try a tool", text: "Shadow work, grounding & more." },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="relative overflow-hidden rounded-3xl">
        <img src={PURPLE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-brand/60" />
        <div className="relative p-8 sm:p-12">
          <p className="text-linen/80 text-sm tracking-wide">Welcome back</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-linen mt-1">Hello, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-linen/90 mt-3 max-w-lg italic font-serif text-xl">"The privilege of a lifetime is to become who you truly are."</p>
        </div>
      </div>

      {(s && (!s.phone_verified || !s.email_verified)) && (
        <Link to="/verify" className="flex items-center gap-3 bg-warmsoft text-warmsoft-text rounded-2xl px-5 py-4" data-testid="verify-banner">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span className="text-sm">Finish verifying your phone and email to keep your safety net fully active.</span>
        </Link>
      )}

      {s && (
        <div className="bg-white rounded-3xl border border-linen-3 p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="checkin-card">
          <div className="flex items-center gap-4 flex-1">
            <span className={`w-14 h-14 rounded-2xl flex items-center justify-center ${s.checked_in_today ? "bg-calm" : "bg-linen-2"}`}>
              {s.checked_in_today ? <CheckCircle2 className="w-7 h-7 text-brand" /> : <Flame className="w-7 h-7 text-accent" />}
            </span>
            <div>
              <p className="font-serif text-3xl text-ink leading-none">{s.streak} <span className="text-lg text-ink-2">day{s.streak === 1 ? "" : "s"}</span></p>
              <p className="text-sm text-ink-2 mt-1">
                {s.checked_in_today ? "You've checked in today — beautifully done." : reminderDue ? "It's time for your daily check-in." : "Your reflection streak."}
              </p>
            </div>
          </div>
          {!s.checked_in_today && (
            <Link to="/app/mood">
              <span className="inline-block bg-brand text-linen rounded-full px-6 py-3 text-sm hover:bg-brand-hover transition-colors" data-testid="checkin-cta">Check in now</span>
            </Link>
          )}
        </div>
      )}

      {reminderDue && (
        <div className="flex items-center gap-3 bg-calm text-calm-text rounded-2xl px-5 py-4" data-testid="reminder-nudge">
          <Flame className="w-5 h-5 shrink-0 text-accent" />
          <span className="text-sm">A gentle nudge: you set a daily check-in for {s.reminder_time}. Take a mindful minute for yourself.</span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        {stats.map((st, i) => (
          <div key={i} className="bg-white rounded-3xl border border-linen-3 p-6 text-center" data-testid={`stat-${i}`}>
            <p className="font-serif text-4xl text-brand">{st.value}</p>
            <p className="text-xs text-ink-mute mt-1">{st.label}</p>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-serif text-3xl text-ink mb-4">Where would you like to begin?</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {quick.map((q, i) => (
            <Link key={i} to={q.to} className="bg-white rounded-3xl border border-linen-3 p-6 flex items-start gap-4 hover:-translate-y-0.5 transition-transform duration-300" data-testid={`quick-${i}`}>
              <span className="w-12 h-12 rounded-2xl bg-calm flex items-center justify-center shrink-0"><q.icon className="w-6 h-6 text-brand" /></span>
              <div>
                <h3 className="font-serif text-2xl text-ink">{q.title}</h3>
                <p className="text-sm text-ink-2 mt-0.5">{q.text}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
